// Whole-app themes. A theme changes the frame, type, corners and depth; the colour profile (palettes.ts)
// and light / dark are separate settings layered on top. Each theme is a set of token blocks in
// tokens.css ([data-ui-theme="…"]) plus a few targeted rules in themes.css. The ids live in
// @technohull/shared (UI_THEME_IDS) so the server schema and public/theme-boot.js accept the same ones.

import { UI_THEME_IDS, type UiThemeId } from '@technohull/shared';

export type { UiThemeId };

export interface UiThemeDef {
  label: string;
  /** one or two plain sentences: what it looks like and who it is for (shown on the Settings tile). */
  purpose: string;
  /** 'none' hides the colour pickers: the theme brings its own colours (Stegi). */
  palette: 'full' | 'none';
  /** 'ink' draws the top-bar mark as flat ink in var(--mark-ink) instead of the chosen logo colourway. */
  mark: 'logo' | 'ink';
  /** false keeps the cursor halo off in this theme without touching the user's own setting. */
  cursorHalo: boolean;
  /** false hides the logo picker (the theme draws the mark itself). */
  logoPicker: boolean;
}

export const UI_THEMES: Record<UiThemeId, UiThemeDef> = {
  evolved: {
    label: 'Evolved',
    purpose: 'The standard look. A frame in your colour, soft depth, near-square corners.',
    palette: 'full', mark: 'logo', cursorHalo: true, logoPicker: true,
  },
  contrast: {
    label: 'High contrast',
    purpose: 'Darker text, firmer lines and a bold focus ring. For glare on the shop floor or tired eyes.',
    palette: 'full', mark: 'logo', cursorHalo: false, logoPicker: true,
  },
  stegi: {
    label: 'Stegi',
    purpose: 'Black and white with one magenta field. Square corners, condensed titles, ruled pages.',
    palette: 'none', mark: 'ink', cursorHalo: false, logoPicker: false,
  },
};

/** the order the Settings tiles show them in. */
export const UI_THEME_ORDER: UiThemeId[] = [...UI_THEME_IDS];

/** resolve a (possibly untrusted / stale) stored value to a valid theme id. */
export function uiThemeId(v: unknown): UiThemeId {
  return typeof v === 'string' && Object.hasOwn(UI_THEMES, v) ? (v as UiThemeId) : 'evolved';
}
